import { PERM_ACTIONS, PERM_NAV, VALIDATION_ACTIONS, PARTNER_ACTIONS, MODULE_ACTION_LEVELS } from "@/lib/constants";
import { Checkbox } from "@/components/ui/checkbox";

const PERM6 = PERM_ACTIONS.filter((a) => a.value !== "configure");
const LETTER_ACTIONS = [["view", "Voir"], ["create", "Créer"], ["edit", "Modifier"], ["delete", "Supprimer"], ["validate", "Valider"],
  ["send", "Envoyer"], ["archive", "Archiver"], ["templates", "Gérer les modèles"], ["doc_settings", "Paramètres documents"], ["ai", "Assistant IA"]];
const LETTER_LEVELS = { aucun: [], basique: ["view"], intermediaire: ["view", "create", "edit", "validate", "send", "archive", "ai"], total: LETTER_ACTIONS.map(([a]) => a) };

function LetterPerms({ value, onChange }) {
  const val = value.courrier;
  const dict = val && typeof val === "object" ? val : typeof val === "string" ? Object.fromEntries((LETTER_LEVELS[val] || []).map((a) => [a, true])) : null;
  return (
    <div className="rounded-lg border p-3" data-testid="perm-row-courrier-actions">
      <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 mb-2">Courriers — droits détaillés{dict === null && <span className="ml-1 normal-case text-slate-400">(hérité du rôle)</span>}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {LETTER_ACTIONS.map(([a, l]) => (
          <label key={a} className="flex items-center gap-1.5 text-xs"><Checkbox checked={!!dict?.[a]} onCheckedChange={() => onChange({ ...value, courrier: { ...(dict || {}), [a]: !dict?.[a] } })} data-testid={`perm-courrier-${a}`} />{l}</label>
        ))}
      </div>
      {dict !== null && <button type="button" onClick={() => { const cp = { ...value }; delete cp.courrier; onChange(cp); }} className="mt-2 text-[10px] text-slate-400 hover:text-rose-500">réinit.</button>}
    </div>
  );
}

function ModuleActionPerms({ value, onChange, module, title, actions }) {
  const val = value[module];
  const lv = MODULE_ACTION_LEVELS[module];
  const dict = val && typeof val === "object" ? val : typeof val === "string" ? Object.fromEntries((lv[val] || []).map((a) => [a, true])) : null;
  return (
    <div className="rounded-lg border p-3" data-testid={`perm-row-${module}-actions`}>
      <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 mb-2">{title} — droits détaillés{dict === null && <span className="ml-1 normal-case text-slate-400">(hérité du rôle)</span>}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {actions.map(([a, l]) => (
          <label key={a} className="flex items-center gap-1.5 text-xs"><Checkbox checked={!!dict?.[a]} onCheckedChange={() => onChange({ ...value, [module]: { ...(dict || {}), [a]: !dict?.[a] } })} data-testid={`perm-${module}-${a}`} />{l}</label>
        ))}
      </div>
      {dict !== null && <button type="button" onClick={() => { const cp = { ...value }; delete cp[module]; onChange(cp); }} className="mt-2 text-[10px] text-slate-400 hover:text-rose-500">réinit.</button>}
    </div>
  );
}

export function levelToDict(level) {
  const map = { aucun: [], basique: ["view", "export"], intermediaire: ["view", "create", "edit", "validate", "export"], total: ["view", "create", "edit", "delete", "validate", "export"] };
  const acts = map[level] || [];
  return acts.reduce((o, a) => ({ ...o, [a]: true }), {});
}

export default function PermMatrix({ value, onChange }) {
  const set = (m, v) => onChange({ ...value, [m]: v });
  const reset = (m) => { const cp = { ...value }; delete cp[m]; onChange(cp); };
  return (
    <div className="space-y-4">
      <LetterPerms value={value} onChange={onChange} />
      <ModuleActionPerms value={value} onChange={onChange} module="validation" title="Validation" actions={VALIDATION_ACTIONS} />
      <ModuleActionPerms value={value} onChange={onChange} module="partenariat" title="Partenariat" actions={PARTNER_ACTIONS} />
      {PERM_NAV.map((grp) => (
        <div key={grp.section} className="rounded-lg border overflow-hidden">
          <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest text-brand-700 border-b">{grp.section}</div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr>
                  <th className="text-left px-3 py-1.5 min-w-44">Menu</th>
                  {PERM6.map((a) => <th key={a.value} className="px-2 py-1.5 text-center">{a.label}</th>)}
                  <th className="px-2"></th>
                </tr>
              </thead>
              <tbody>
                {grp.items.filter((item) => item.module !== "courrier").map((item, idx) => {
                  const m = item.module;
                  const val = value[m];
                  const dict = val && typeof val === "object" ? val : (typeof val === "string" ? levelToDict(val) : null);
                  const inherited = dict === null;
                  const toggle = (a) => { const base = dict || {}; set(m, { ...base, [a]: !base[a] }); };
                  return (
                    <tr key={`${m}-${idx}`} className="border-t" data-testid={`perm-row-${m}`}>
                      <td className="px-3 py-1.5 font-medium">{item.label}{inherited && <span className="ml-1 text-[10px] text-slate-400">(hérité)</span>}</td>
                      {PERM6.map((a) => (
                        <td key={a.value} className="px-2 py-1.5 text-center">
                          <Checkbox checked={!!(dict && dict[a.value])} onCheckedChange={() => toggle(a.value)} data-testid={`perm-${m}-${a.value}`} />
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-center">{!inherited && <button type="button" onClick={() => reset(m)} className="text-[10px] text-slate-400 hover:text-rose-500">réinit.</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
